// Sheet UI helpers copied verbatim from the Cr8Her dashboard's Book a call funnel page (pages/BookedCallFunnel.tsx),
// so Urban Unity's Velocity Metrics renders exactly like Cr8Her's (Joey Forslund, Oct 29, 2026: "the exact same, literally to a T").
import type { MetricBenchmark } from "../config/booked-call-metric-benchmarks";
export { sheetWindowsForRange } from "./sheet-windows";

export type TargetStatus = "above" | "within" | "below" | "unavailable";
export type SheetExportRow = { section: string; metric: string; definition: string; target: string; benchmark?: MetricBenchmark; benchmarks: Array<MetricBenchmark | undefined>; targets: string[]; values: string[]; rawValues: Array<number | null>; statusValues: Array<number | null> };

export function targetStatus(value: number | null, benchmark?: MetricBenchmark): TargetStatus {
  if (value === null || value === undefined || !benchmark?.direction) return "unavailable";
  if (benchmark.direction === "min") return value >= (benchmark.min ?? Infinity) ? "above" : "below";
  if (benchmark.direction === "max") return value <= (benchmark.max ?? -Infinity) ? "above" : "below";
  if (value < (benchmark.min ?? -Infinity)) return "below";
  if (value > (benchmark.max ?? Infinity)) return "above";
  return "within";
}

export const targetStatusStyle: Record<TargetStatus, { label: string; color: string }> = {
  above: { label: "Above target", color: "#16a34a" },
  within: { label: "On target", color: "#ca8a04" },
  below: { label: "Below target", color: "#dc2626" },
  unavailable: { label: "N/A", color: "#9ca3af" },
};

export function TargetStatusIcon({ status }: { status: TargetStatus }) {
  const color = targetStatusStyle[status].color;
  if (status === "above") return <svg aria-hidden="true" width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,11 5.5,7 8.5,9 12,3" /><polyline points="9,3 12,3 12,6" /></svg>;
  if (status === "below") return <svg aria-hidden="true" width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,3 5.5,7 8.5,5 12,11" /><polyline points="9,11 12,11 12,8" /></svg>;
  if (status === "within") return <svg aria-hidden="true" width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7" cy="7" r="5.5" /><polyline points="4.5,7.5 6.5,9.5 9.5,5" /></svg>;
  return null;
}

export function targetMarker(status: TargetStatus) {
  return status === "above" ? "↑" : status === "below" ? "↓" : status === "within" ? "✓" : "";
}

function pdfEscape(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)").replace(/[–—]/g, "-");
}

export function buildTablePdf(headers: string[], sections: Array<{ title: string; rows: SheetExportRow[] }>, caption = "Completed Eastern business weeks", title = "Book a Call Funnel — Sheets") {
  // Legal landscape keeps the PDF's # / Metric / four weekly-column geometry aligned with Sheets.
  const pageWidth = 1008; const pageHeight = 612; const left = 32; const right = 32;
  const columnWidths = [38, 258, ...headers.map(() => (pageWidth - left - right - 296) / headers.length)];
  const headerHeight = 24; const rowHeight = 19; const firstRowY = 502; const lastRowY = 58;
  const rowsPerPage = Math.floor((firstRowY - lastRowY) / rowHeight) - 1;
  const pageContents: string[] = [];
  let rowNumber = 1;
  const text = (x: number, y: number, value: string, size = 9, bold = false, color = "0.13 0.13 0.15") => `BT /F${bold ? "2" : "1"} ${size} Tf ${color} rg 1 0 0 1 ${x} ${y} Tm (${pdfEscape(value)}) Tj ET`;
  const horizontalLine = (y: number) => `0.78 G 0.45 w ${left} ${y} m ${pageWidth - right} ${y} l S`;
  const verticalLines = (bottom: number, top: number) => {
    let x = left; const commands = [`0.78 G 0.45 w ${x} ${bottom} m ${x} ${top} l S`];
    columnWidths.forEach((width) => { x += width; commands.push(`${x} ${bottom} m ${x} ${top} l S`); });
    return commands;
  };
  const addPage = (section: { title: string; rows: SheetExportRow[] }, pageRows: SheetExportRow[], continued: boolean) => {
    const commands = [text(left, 572, title, 15, true), text(left, 554, `${section.title}${continued ? " (continued)" : ""}`, 10, true, "0.23 0.31 0.44"), "0.23 0.31 0.44 rg", `${left} ${firstRowY} ${pageWidth - left - right} ${headerHeight} re f`];
    let x = left + 7;
    ["#", "Metric", ...headers].forEach((heading, index) => { commands.push(text(x, firstRowY + 8, heading, 8, true, "1 1 1")); x += columnWidths[index]; });
    const tableBottom = firstRowY - headerHeight - pageRows.length * rowHeight;
    commands.push(...verticalLines(tableBottom, firstRowY + headerHeight), horizontalLine(firstRowY), horizontalLine(firstRowY + headerHeight));
    pageRows.forEach((row, index) => {
      const y = firstRowY - headerHeight - index * rowHeight;
      const values = [String(rowNumber++), row.metric, ...row.values];
      let cellX = left + 7;
      values.forEach((value, cellIndex) => {
        const valueWidth = columnWidths[cellIndex];
        // In every week column, the measured value begins at the same left edge as the Sheet.
        commands.push(text(cellX, y - 13, value, 8, cellIndex === 1));
        if (cellIndex >= 2 && row.benchmarks[cellIndex - 2]?.target) {
          const weekIndex = cellIndex - 2;
          const status = targetStatus(row.statusValues[weekIndex] ?? null, row.benchmarks[weekIndex]);
          const marker = targetMarker(status);
          const target = `(${marker ? `${marker} ` : ""}${row.targets[weekIndex] ?? row.target})`;
          const clippedTarget = target.length > 22 ? `${target.slice(0, 19)}...)` : target;
          const targetX = cellX + valueWidth - 8 - clippedTarget.length * 4.2;
          const pdfColor: Record<TargetStatus, string> = { above: "0.09 0.64 0.16", within: "0.79 0.54 0.02", below: "0.86 0.15 0.15", unavailable: "0.61 0.64 0.69" };
          commands.push(text(Math.max(cellX + 56, targetX), y - 13, clippedTarget, 7, true, pdfColor[status]));
        }
        cellX += valueWidth;
      });
      commands.push(horizontalLine(y - rowHeight));
    });
    commands.push(text(left, 28, `Urban Unity · ${caption}`, 7, false, "0.35 0.37 0.4"));
    pageContents.push(commands.join("\n"));
  };
  sections.forEach((section) => {
    for (let start = 0; start < section.rows.length; start += rowsPerPage) addPage(section, section.rows.slice(start, start + rowsPerPage), start > 0);
  });
  const objects: string[] = ["<< /Type /Catalog /Pages 2 0 R >>", ""];
  const pageObjectNumbers = pageContents.map((_, index) => 3 + index * 2);
  objects[1] = `<< /Type /Pages /Kids [${pageObjectNumbers.map((n) => `${n} 0 R`).join(" ")}] /Count ${pageContents.length} >>`;
  pageContents.forEach((content, index) => {
    const pageNumber = 3 + index * 2; const contentNumber = pageNumber + 1;
    objects[pageNumber - 1] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${pageContents.length * 2 + 3} 0 R /F2 ${pageContents.length * 2 + 4} 0 R >> >> /Contents ${contentNumber} 0 R >>`;
    objects[contentNumber - 1] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>", "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");
  let pdf = "%PDF-1.4\n"; const offsets = [0];
  objects.forEach((object, index) => { offsets.push(pdf.length); pdf += `${index + 1} 0 obj\n${object}\nendobj\n`; });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => { pdf += `${String(offset).padStart(10, "0")} 00000 n \n`; });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}
